import { TableOutlined } from '@ant-design/icons';
import { Segmented } from 'antd';
import { ReactComponent as BarGraphIcon } from 'assets/icons/BarGraph.svg';
import { ReactComponent as MapMenuIcon } from 'assets/icons/MapMenu.svg';
import { ReactComponent as PresIcon } from 'assets/icons/Presentation.svg';
import { useAuthContext } from 'contexts/AuthContext';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AccessType } from 'types/User';
import { DossiersListViewMode } from 'views/dossiers/list/dossiersList.types';

export type ViewMode = DossiersListViewMode;

interface ViewToggleProps {
  value: ViewMode;
  onChange: (value: ViewMode) => void;
  className?: string;
  compact?: boolean;
}

export default function ViewToggle({
  value,
  onChange,
  className,
  compact = false,
}: ViewToggleProps) {
  const { t } = useTranslation();
  const isConcessionnaire =
    useAuthContext().auth?.user?.accessType === AccessType.CONCESSIONNAIRE;

  const options = React.useMemo(
    () =>
      [
        {
          label: (
            <div className="flex gap-2 items-center px-1 md:px-0">
              <TableOutlined />
              {!compact && t('global:vue_tableau')}
            </div>
          ),
          value: 'table' as ViewMode,
        },
        !isConcessionnaire && {
          label: (
            <div className="flex gap-2 items-center px-1 md:px-0">
              <PresIcon />
              {!compact && t('global:vue_graphique')}
            </div>
          ),
          value: 'chart' as ViewMode,
        },
        {
          label: (
            <div className="flex gap-2 items-center px-1 md:px-0">
              <BarGraphIcon />
              {!compact && t('global:vue_statut')}
            </div>
          ),
          value: 'status' as ViewMode,
        },
        {
          label: (
            <div className="flex gap-2 items-center px-1 md:px-0">
              <MapMenuIcon aria-hidden data-testid="map-view-icon" />
              {!compact && t('global:vue_carte')}
            </div>
          ),
          value: 'map' as ViewMode,
        },
      ].filter(Boolean) as { label: React.ReactNode; value: ViewMode }[],
    [compact, isConcessionnaire, t],
  );

  return (
    <Segmented
      value={value}
      onChange={onChange}
      options={options}
      className={`view-toggle ${className ?? ''}`.trim()}
      size={compact ? 'large' : 'small'}
    />
  );
}
